/**
 * Marketing Review module contracts.
 *
 * ⚠️ These shapes are frontend-defined. The Go backend for this module does not exist yet, so
 * nothing here has been cross-checked against a server struct. When the API lands, verify every
 * field name and the `{ status, data: { responsedata } }` envelope against the backend repo
 * before wiring `MarketingReviewService` to real endpoints.
 */

// ── Enumerations ──────────────────────────────────────────────────────────────

/** Lifecycle of an advertisement submission. */
export type AdStatus =
    | 'Draft'
    | 'Submitted'
    | 'In_Review'
    | 'Approved'
    | 'Returned'
    | 'Rejected';

/** Channel the advertisement is intended to run on. */
export type AdChannel =
    | 'Social Media'
    | 'Print'
    | 'Email'
    | 'Webinar'
    | 'Website'
    | 'Collateral';

/** Outcome a reviewer can record against an advertisement. */
export type ReviewDecision = 'Approved' | 'Returned' | 'Rejected';

/**
 * Type of marketing material, as the codes printed on the paper Marketing Materials Application
 * Form. The code is what is stored and what the approval reference is built from — the full term
 * is presentation only, so the two are kept together in `AD_MATERIAL_TYPES`.
 *
 * ⚠️ Frontend-defined, taken from the business's abbreviation table. Cross-check against the
 * backend's enum when this module is wired up.
 */
export type AdMaterialType =
    | 'NPA'
    | 'EI'
    | 'DNL'
    | 'ENL'
    | 'EDM'
    | 'DM'
    | 'PM'
    | 'PS'
    | 'OM'
    | 'FM'
    | 'WEB'
    | 'TS'
    | 'RS'
    | 'OTH'
    | 'MA'
    | 'ADV'
    | 'PRS'
    | 'SCP'
    | 'FLY'
    | 'BRO'
    | 'SMS'
    | 'WEC'
    | 'SCM'
    | 'OAR'
    | 'VID'
    | 'SCT';

/**
 * Broad family of an attached file. Drives the badge, the icon and — most importantly — which
 * preview renderer the create and review screens reach for.
 *
 * `slides`, `document` and `spreadsheet` are kept as separate kinds rather than lumped into
 * `other`: the review page renders `.docx`, `.pptx` and sheets in the browser, and the legacy
 * `.doc`/`.ppt` formats fall back to a typed document card with an open-in-new-tab action.
 */
export type AdFileKind =
    | 'image'
    | 'video'
    | 'pdf'
    | 'slides'
    | 'document'
    | 'spreadsheet'
    | 'other';

/** Reviewer response to a single evaluation criterion. */
export type CriterionResponse = 'Yes' | 'No' | 'NA' | '';

// ── Advertisement ─────────────────────────────────────────────────────────────

/** A creative file attached to an advertisement. */
export interface AdAttachment {
    id: number;
    fileName: string;
    fileType: string;
    fileSizeKb: number;
    /** Object URL (mock) or server-issued download URL. */
    url: string;
}

/** A single scored line of the reviewer's evaluation checklist. */
export interface AdReviewCriterion {
    key: string;
    label: string;
    response: CriterionResponse;
    remarks: string;
}

/**
 * A plain comment on a submission — no decision attached.
 *
 * Kept separate from `AdReview` so the branch and the marketing team can talk before anyone
 * commits to an outcome. A comment can be edited or withdrawn by its author; a recorded decision
 * cannot.
 */
export interface AdComment {

    id: number;
    authorName: string;
    authorRole: string;
    /** ISO date-time. */
    postedOn: string;
    /** ISO date-time of the last edit, `null` when never edited. */
    editedOn: string | null;
    text: string;

    /** Where on the material this comment is pinned. Absent on a plain thread comment. */
    pin?: AdCommentPin;
    /** Badge number shown on the material and the card. Stable — not reused after a delete. */
    pinNo?: number;
    /** Pinned comments only. A plain comment has nothing to resolve. */
    status?: AdCommentStatus;
    resolvedBy?: string;
    /** ISO date-time. */
    resolvedOn?: string;
    /** One level of replies. A reply never carries a pin of its own. */
    replies?: AdComment[];
}

export type AdCommentStatus = 'Open' | 'Resolved';

/** `text` anchors a highlighted selection; `area` anchors a box the reviewer drew. */
export type AdPinKind = 'text' | 'area';

/** A box on one page, as fractions (0–1) of that page's width and height, so it survives zoom. */
export interface AdPinRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * Anchors a comment to one spot in one file.
 *
 * "Page" means whatever the renderer splits the file into — a PDF or Word page, a slide, a
 * worksheet, or the whole image.
 *
 * ⚠️ Frontend-defined. Cross-check against the backend struct when `saveMarketingAdComment` lands.
 */
export interface AdCommentPin {
    /** `null` for the key-visual tab, which has no attachment row. */
    documentId: number | null;
    /** 1-based page, slide or sheet index. */
    page: number;
    /** `Page 2`, `Slide 3`, `Sheet: Rates`, `Image`. */
    pageLabel: string;
    kind: AdPinKind;
    /** One box for an area pin; one per line for a text selection. */
    rects: AdPinRect[];
    /** The selected words, for a text pin. */
    quote?: string;
    /** Spreadsheets only — the cell a text pin was made in, e.g. `B4`. */
    cellRef?: string;
}

/** One completed evaluation. Reviews accumulate so the discussion thread is preserved. */
export interface AdReview {
    id: number;
    reviewerName: string;
    reviewerRole: string;
    /** ISO date-time. */
    reviewedOn: string;
    decision: ReviewDecision;
    overallComments: string;
    criteria: AdReviewCriterion[];
}

/** An advertisement submitted for marketing review. */
export interface MarketingAd {
    id: number;
    referenceNo: string;
    title: string;
    branches: string[];
    targetRecipients: string;
    expectedDisseminationDate: string;
    disseminationNote: string;
    modeOfDissemination: string;
    materialType: AdMaterialType | '';
    channel: AdChannel;
    category: string;
    targetAudience: string;
    campaignStartDate: string;
    campaignEndDate: string;
    description: string;
    bodyCopy: string;
    callToAction: string;
    disclaimers: string;
    previewImageUrl: string;
    attachments: AdAttachment[];
    status: AdStatus;
    createdBy: string;
    createdByRepCode: string;
    createdOn: string;
    submittedOn: string | null;
    lastReviewedOn: string | null;
    reviews: AdReview[];
    comments: AdComment[];

}

export interface MarketingAdDetailsby {
    MarketingReviewApplication: MarketingAdDetails;
}

export interface adReviewComments {
    pin?:AdCommentPin;
    pinNo?: number;
    commentedBy: string;
    piasRepCode:string;
    createdDate: string;
    text: string;
    userRole: string;
    marketingReviewApplicationId: number;
    updatedDate: string;
    resolvedDate:string;
    resolvedBy:string;
    status:string;
    replayId:number;
    replayCommend:string;
    adId: number;
}

export interface documents {
    documentId:number;
    documentDate:string;
    documentName:string;
    accesspoint:string;
    url:string;
    
}

export interface MarketingAdDetails {
    marketingReviewApplicationId: number;
    applicantRepCode: string;
    applicantBranchId: number;
    branchName:string;
    marketingTitle: string;
    dateOfApplication: string;
    marketingMaterialTypeId: number;
    targetRecepients: string;
    expectedDateOfDissemination: string;
    expectedCommentOfDissemination: string;
    modeOfDissemination: string;
    approvalNo: string;
    dateIssuedByMarketing: string;
    issuerRepCode: string;
    status: AdStatus;
    submittedBy: string;
    submittedByRepCode: string;
    documents:documents[];
    edmExpectedDate: string;
    referenceId: number;
    complianceByName: string;
    complianceRepCode: string;
    createdDate: string;
    updatedDate: string;
}

// ── Payloads ──────────────────────────────────────────────────────────────────

/** Create / edit payload — `MarketingAd` without the server-owned fields. */
export interface SaveAdPayload {
    /** Present when editing an existing draft, omitted when creating. */
    id?: number;
    title: string;
    branches: string[];
    targetRecipients: string;
    expectedDisseminationDate: string;
    disseminationNote: string;
    modeOfDissemination: string;
    materialType: AdMaterialType | '';
    channel: AdChannel;
    category: string;
    targetAudience: string;
    campaignStartDate: string;
    campaignEndDate: string;
    description: string;
    bodyCopy: string;
    callToAction: string;
    disclaimers: string;
    previewImageUrl: string;
    attachments: AdAttachment[];
}

// ── List / dashboard views ────────────────────────────────────────────────────

/**
 * Ads-list filter state. Persisted as raw JSON in `sessionStorage` (ephemeral view state).
 *
 * `branch` stays a single name: an ad matches when that name is among its `branches`.
 */
export interface AdsListFilter {
    search: string;
    status: AdStatus | '';
    channel: AdChannel | '';
    branch: string;
}

/** Per-branch rollup shown on the dashboard. */
export interface BranchAdSummary {
    branch: string;
    total: number;
    pendingReview: number;
    approved: number;
    returned: number;
    rejected: number;
    /** ISO date-time of the branch's latest submission, `null` if it has none. */
    lastSubmittedOn: string | null;
}

/** Dashboard KPI tiles plus the branch rollup. */
export interface MarketingReviewSummary {
    totalAds: number;
    pendingReview: number;
    approved: number;
    /** `Returned` — surfaced as "Changes requested" on the dashboard tiles. */
    returned: number;
    rejected: number;
    branches: BranchAdSummary[];
}

/**
 * One row of the dashboard's review queue: an advertisement that has reached the marketing team,
 * with the presentation bits the row needs resolved up front.
 *
 * Expanding a row lists `files` — the formats attached to *that* advertisement. Drafts never
 * appear here; see `REVIEW_QUEUE_STATUSES`.
 */
export interface ReviewQueueRow {
    ad: MarketingAd;
    /** Two-letter avatar initials for the submitter. */
    initials: string;
    /** Distinct format badges across the ad's files, e.g. `['PPT', 'JPG']`. */
    formats: string[];
    /** The ad's attachments, with their kind resolved for the nested rows. */
    files: ReviewQueueFile[];
    /** Days since submission — used to flag anything left sitting in the queue. */
    daysWaiting: number;
}

/** One attached file under an expanded queue row. */
export interface ReviewQueueFile {
    attachment: AdAttachment;
    kind: AdFileKind;
    /** Short badge, e.g. `XLS`. */
    format: string;
    icon: string;
    tone: string;
    /** `XLS · 11 KB` */
    meta: string;
    /** Set for image files, so the row shows a thumbnail instead of an icon. */
    thumbnailUrl: string;
}

/** One line of the audit log dialog. */
export interface AdAuditEntry {
    /** ISO date-time. */
    at: string;
    action: string;
    detail: string;
    actor: string;
    /** Palette key for the timeline dot: `submitted`, `approved`, `returned`, `rejected`, `info`. */
    tone: 'submitted' | 'approved' | 'returned' | 'rejected' | 'info';
}

// ── Reference data ────────────────────────────────────────────────────────────

/**
 * The evaluation checklist a reviewer scores. Single source of truth so the review page and
 * the creator's self-declaration stay aligned.
 */
export const AD_REVIEW_CRITERIA: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'accuracy', label: 'Product and performance claims are accurate and substantiated' },
    { key: 'disclaimers', label: 'Required disclaimers and disclosures are present and legible' },
    { key: 'branding', label: 'Branding, logo usage and tone follow PIAS brand guidelines' },
    { key: 'regulatory', label: 'Content complies with MAS advertising requirements' },
    { key: 'audience', label: 'Targeting and channel are appropriate for the stated audience' },
    { key: 'contact', label: 'Adviser name, rep code and contact details are correct' },
];

/** Selectable channels, in display order. */
export const AD_CHANNELS: ReadonlyArray<AdChannel> = [
    'Social Media',
    'Print',
    'Email',
    'Webinar',
    'Website',
    'Collateral',
];

/** Selectable statuses, in lifecycle order. */
export const AD_STATUSES: ReadonlyArray<AdStatus> = [
    'Draft',
    'Submitted',
    'In_Review',
    'Returned',
    'Approved',
    'Rejected',
];

/**
 * Every selectable type of marketing material, in the order the business's abbreviation table
 * lists them — deliberately not alphabetised, so a submitter reading from the paper form finds a
 * code where they expect it.
 */
export const AD_MATERIAL_TYPES: ReadonlyArray<{ code: AdMaterialType; label: string }> = [
    { code: 'NPA', label: 'Newspaper Article' },
    { code: 'EI', label: 'Electronic Invite' },
    { code: 'DNL', label: 'Direct Newsletter' },
    { code: 'ENL', label: 'Electronic Newsletter' },
    { code: 'EDM', label: 'Electronic Direct Mailer' },
    { code: 'DM', label: 'Direct Mailer' },
    { code: 'PM', label: 'Placemat' },
    { code: 'PS', label: 'Presentation Slides' },
    { code: 'OM', label: 'Online Material' },
    { code: 'FM', label: 'Form' },
    { code: 'WEB', label: 'Website' },
    { code: 'TS', label: 'Telemarketing Script (strictly for FAR)' },
    { code: 'RS', label: 'Roadshow' },
    { code: 'OTH', label: 'Others' },
    { code: 'MA', label: 'Magazine Article' },
    { code: 'ADV', label: 'Advertisement' },
    { code: 'PRS', label: 'Press Release' },
    { code: 'SCP', label: 'Social Media Post' },
    { code: 'FLY', label: 'Flyer' },
    { code: 'BRO', label: 'Brochure' },
    { code: 'SMS', label: 'Short Message Service' },
    { code: 'WEC', label: 'WeChat (Ad, Article, etc.)' },
    { code: 'SCM', label: 'Social Media Pages' },
    { code: 'OAR', label: 'Online Article' },
    { code: 'VID', label: 'Video' },
    { code: 'SCT', label: 'Script' },
];

/** Full term for a material type code, for the lists and the review page. `''` when unset. */
export const AD_MATERIAL_TYPE_LABELS: Record<string, string> = AD_MATERIAL_TYPES
    .reduce((labels, type) => ({ ...labels, [type.code]: type.label }), {} as Record<string, string>);

/** Ad categories offered on the create form. */
export const AD_CATEGORIES: ReadonlyArray<string> = [
    'Product Promotion',
    'Event / Webinar',
    'Recruitment',
    'Brand Awareness',
    'Educational Content',
    'Festive Greeting',
];

/**
 * Selectable branches for the application's branch picker, in display order.
 *
 * ⚠️ Mock reference data. Agent Maintenance already serves the real list from
 * `environment.apis.getBranchName` (`/v2/pias/agentmaintenance/getbranch`) — point
 * `MarketingReviewService.listBranches()` at it once this module is wired to the backend.
 */
export const AD_BRANCHES: ReadonlyArray<string> = [
    'Apex Infinite',
    'BDM',
    'Bishan',
    'Jurong',
    'Orchard',
    'Raffles',
    'Tampines',
];

/**
 * Accepted upload formats, as the business stated them: video, GIF, PDF, PPT, JPEG, PNG, Word
 * and Excel. `accept` extensions are listed alongside the badge so the file picker, the
 * client-side guard and the "also accepted" chips all read from one place.
 */
export const AD_ACCEPTED_FORMATS: ReadonlyArray<{ label: string; extensions: string[] }> = [
    { label: 'VIDEO', extensions: ['.mp4', '.mov', '.webm', '.m4v'] },
    { label: 'GIF', extensions: ['.gif'] },
    { label: 'PDF', extensions: ['.pdf'] },
    { label: 'PPT', extensions: ['.ppt', '.pptx'] },
    { label: 'JPEG', extensions: ['.jpg', '.jpeg'] },
    { label: 'PNG', extensions: ['.png'] },
    { label: 'WORD', extensions: ['.doc', '.docx'] },
    { label: 'EXCEL', extensions: ['.xls', '.xlsx'] },
];

/** Comma-separated `accept` attribute covering every format in `AD_ACCEPTED_FORMATS`. */
export const AD_ACCEPT_ATTRIBUTE: string = AD_ACCEPTED_FORMATS
    .flatMap((format) => format.extensions)
    .join(',');

/**
 * Presentation per file kind: the badge shown when no more specific format is known, the Material
 * icon ligature, and the palette key its document card is tinted with.
 */
export const AD_FILE_KIND_META: Record<AdFileKind, { label: string; icon: string; tone: string }> = {
    image: { label: 'IMAGE', icon: 'image', tone: 'image' },
    video: { label: 'VIDEO', icon: 'movie', tone: 'video' },
    pdf: { label: 'PDF', icon: 'picture_as_pdf', tone: 'pdf' },
    slides: { label: 'PPT', icon: 'slideshow', tone: 'slides' },
    document: { label: 'DOC', icon: 'article', tone: 'document' },
    spreadsheet: { label: 'XLS', icon: 'table_chart', tone: 'spreadsheet' },
    other: { label: 'FILE', icon: 'attach_file', tone: 'other' },
};

/** Material icon ligature per channel, for list and detail rows. */
export const AD_CHANNEL_ICONS: Record<AdChannel, string> = {
    'Social Media': 'share',
    'Print': 'print',
    'Email': 'email',
    'Webinar': 'videocam',
    'Website': 'language',
    'Collateral': 'description',
};

/** Statuses that still need a reviewer to act. */
export const PENDING_REVIEW_STATUSES: ReadonlyArray<AdStatus> = ['Submitted', 'In_Review'];

/**
 * Statuses that have reached the marketing team — everything a submitter has actually sent, plus
 * the outcomes that follow. `Draft` is excluded: an unsent draft is the submitter's own business
 * and has no place in the review queue or its counts.
 */
export const REVIEW_QUEUE_STATUSES: ReadonlyArray<AdStatus> = [
    'Submitted',
    'In_Review',
    'Returned',
    'Approved',
    'Rejected',
];

/** Statuses whose review is closed — the review form opens read-only. */
export const CLOSED_REVIEW_STATUSES: ReadonlyArray<AdStatus> = ['Approved', 'Rejected'];

/**
 * `userRole` values (from `SessionStorageService.getCurrentUserRole()`) that make up the marketing
 * team. They get the reviewer dashboard — every user's submitted ads, no "Create Ad". Everyone
 * else with access to the module gets the creator dashboard — their own ads, drafts included.
 *
 * ⚠️ PLACEHOLDER — empty until the marketing team's role name(s) are confirmed, so every user
 * currently gets the creator view. Fill this in once the roles are known (e.g. `['Marketing']`).
 */
export const MARKETING_REVIEWER_ROLES: ReadonlyArray<string> = [];

export interface MarketingReviewApplication {

    marketingReviewApplicationId: number;
    applicantRepCode: string;
    applicantBranchId: number;
    marketingTitle: string;
    dateOfApplication: string;
    marketingMaterialTypeId: number;
    targetRecepients: string;
    expectedDateOfDissemination: string;
    expectedCommentOfDissemination: string;
    modeOfDissemination: string;
    approvalNo: string;
    dateIssuedByMarketing: string;
    issuerRepCode: string;
    status: string;
    submittedBy: string;
    submittedByRepCode: string;
    edmExpectedDate: string;
    referenceId: number;
    complianceByName: string;
    complianceRepCode: string;
    createdDate: string;
    updatedDate: string;
    documents?: {
        documentDate: string;
        documentId: number;
        documentName: string;
    }[];

}
